import { useEffect, useMemo, useState } from "react";
import { BookOpen, ExternalLink, FileText, FlaskConical, Landmark, Loader2, Receipt, ScrollText, Search, Sparkles, Database } from "lucide-react";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const TYPE_META = {
  "EPD": { label: "Basis Data EPD/LCA", icon: FlaskConical, color: "emerald" },
  "JURNAL": { label: "Jurnal Ilmiah", icon: BookOpen, color: "sky" },
  "STANDAR": { label: "Standar (SNI/ISO)", icon: ScrollText, color: "amber" },
  "PEMERINTAH": { label: "Regulasi Pemerintah", icon: Landmark, color: "indigo" },
  "HASIL PENELITIAN": { label: "Hasil Penelitian", icon: FlaskConical, color: "emerald" },
  "HARGA PASAR": { label: "Survei Harga Pasar", icon: Receipt, color: "rose" },
  "ASUMSI EKONOMI SIMULASI": { label: "Asumsi Ekonomi (Simulasi)", icon: FileText, color: "slate" },
  "ASUMSI SIMULASI": { label: "Asumsi Simulasi", icon: FileText, color: "slate" },
};

const TABS = [
  { key: "ALL", label: "Semua Sumber" },
  { key: "EPD", label: "EPD / LCA" },
  { key: "JURNAL", label: "Jurnal Ilmiah" },
  { key: "STANDAR", label: "Standar" },
  { key: "PEMERINTAH", label: "Pemerintah" },
  { key: "HARGA PASAR", label: "Harga Pasar" },
  { key: "ASUMSI EKONOMI SIMULASI", label: "Asumsi" },
];

function colorClasses(color) {
  const map = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    sky: "bg-sky-50 text-sky-700 border-sky-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    slate: "bg-slate-100 text-slate-700 border-slate-200",
  };
  return map[color] || map.slate;
}

function RefCard({ item }) {
  const meta = TYPE_META[item.source_type] || TYPE_META["ASUMSI SIMULASI"];
  const Icon = meta.icon;
  const isAssumption = String(item.source_type || "").startsWith("ASUMSI");

  return (
    <article
      className="group relative bg-white border border-slate-200 rounded-2xl p-5 hover:border-emerald-400 hover:shadow-md transition-all"
      data-testid={`ref-card-${item.id}`}
    >
      <div className="flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl flex items-center justify-center border ${colorClasses(meta.color)}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full border ${colorClasses(meta.color)}`}>
              {meta.label}
            </span>
            {isAssumption && (
              <span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                Data Demo
              </span>
            )}
            {item.publication_year && (
              <span className="text-[11px] text-slate-500 font-mono">{item.publication_year}</span>
            )}
          </div>
          <h3 className="mt-1.5 font-semibold text-slate-900 leading-tight">{item.source_title}</h3>
          {item.authors_or_institution && (
            <p className="text-xs text-slate-600 mt-0.5 italic">{item.authors_or_institution}</p>
          )}
          {item.journal_or_publisher && (
            <p className="text-xs text-slate-500 mt-0.5">{item.journal_or_publisher}</p>
          )}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Parameter Diambil</div>
          <div className="text-slate-800 font-medium">{item.parameter_taken}</div>
        </div>
        {item.page_or_table && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Halaman / Tabel</div>
            <div className="text-slate-700">{item.page_or_table}</div>
          </div>
        )}
        {(item.original_value || item.original_unit) && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Nilai Asli</div>
            <div className="text-slate-700 font-mono">
              {item.original_value} {item.original_unit && <span className="text-slate-500">{item.original_unit}</span>}
            </div>
          </div>
        )}
        {(item.value_used || item.unit_used) && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Nilai Yang Dipakai</div>
            <div className="text-emerald-700 font-mono font-bold">
              {item.value_used} {item.unit_used && <span className="text-slate-500 font-normal">{item.unit_used}</span>}
            </div>
          </div>
        )}
      </div>

      {item.notes && (
        <div className="mt-3 text-xs text-slate-600 bg-slate-50 border-l-2 border-slate-300 pl-3 py-1.5 rounded-r">
          {item.notes}
        </div>
      )}

      <div className="mt-4 flex items-center justify-between text-[11px]">
        <span className="text-slate-400">
          {item.date_accessed ? `Diakses: ${item.date_accessed}` : ""}
        </span>
        {item.doi_or_url && (
          <a
            href={item.doi_or_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-semibold group-hover:underline"
            data-testid={`ref-link-${item.id}`}
          >
            Buka Sumber <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </article>
  );
}

export default function DataReferences() {
  const { user } = useAuth();
  const isSuperAdmin = user?.platform_role === "super_admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ALL");
  const [search, setSearch] = useState("");
  const [seedOpen, setSeedOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/data-references");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const runSeed = async () => {
    setSeeding(true);
    setSeedResult(null);
    try {
      const { data } = await api.post("/admin/seed-simulation");
      setSeedResult(data?.summary || null);
      toast.success("Seed simulasi ilmiah berhasil dijalankan.");
      // Only reload references if user is currently in the seeded org
      if (user?.active_org_id === data?.summary?.org_id) {
        await loadData();
      }
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSeeding(false);
    }
  };

  const counts = useMemo(() => {
    const acc = { ALL: items.length };
    for (const it of items) {
      const k = it.source_type || "-";
      acc[k] = (acc[k] || 0) + 1;
    }
    return acc;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (tab !== "ALL" && it.source_type !== tab) return false;
      if (!q) return true;
      return [
        it.source_title, it.authors_or_institution, it.journal_or_publisher,
        it.parameter_taken, it.notes, it.doi_or_url, it.value_used, it.unit_used,
      ].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, tab, search]);

  return (
    <div className="space-y-6" data-testid="data-references-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-emerald-700">
            <Sparkles className="w-3.5 h-3.5" /> Provenance Data Ilmiah
          </div>
          <h1 className="mt-1 text-2xl md:text-3xl font-bold text-slate-900">Referensi Data Simulasi</h1>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            Daftar sumber ilmiah (EPD, jurnal, standar SNI, regulasi, survei) yang menjadi dasar setiap parameter numerik
            dalam simulasi GREENCON HPP. Setiap entri berisi kutipan, DOI/URL, dan nilai persis yang dipakai — sehingga
            audit dan verifikasi akademik dapat dilakukan langsung dari platform.
          </p>
        </div>
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 px-4 py-3 text-xs text-emerald-900 min-w-[240px]">
          <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Total Referensi</div>
          <div className="mt-0.5 text-2xl font-bold">{items.length}</div>
          <div className="text-[11px] text-emerald-700/80">
            Terhubung ke master data, mix design, dan perhitungan HPP.
          </div>
          {isSuperAdmin && (
            <button
              onClick={() => setSeedOpen(true)}
              className="mt-3 w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold uppercase tracking-widest transition-colors"
              data-testid="btn-seed-simulation"
            >
              <Database className="w-3.5 h-3.5" /> Seed Data Simulasi
            </button>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex-1 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari judul, penulis, DOI, atau parameter…"
            className="w-full h-10 pl-9 pr-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:border-emerald-500"
            data-testid="ref-search-input"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TABS.map((t) => {
            const c = counts[t.key] ?? 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold border transition-colors ${
                  active
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400 hover:text-emerald-700"
                }`}
                data-testid={`ref-tab-${t.key}`}
              >
                {t.label}
                <span className={`text-[10px] rounded-full px-1.5 py-0.5 ${active ? "bg-white/20" : "bg-slate-100 text-slate-500"}`}>
                  {c}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Memuat referensi…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-2xl bg-white">
          <ScrollText className="w-8 h-8 text-slate-300 mx-auto" />
          <div className="mt-3 text-sm text-slate-600">
            Belum ada referensi yang cocok. Coba kata kunci lain atau jalankan seed simulasi ilmiah.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ref-grid">
          {filtered.map((it) => <RefCard key={it.id} item={it} />)}
        </div>
      )}

      {/* Footnote */}
      <div className="text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-xl p-3">
        <span className="font-semibold text-slate-700">Catatan Akademik:</span> Nilai berlabel
        <span className="mx-1 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-bold uppercase">Data Demo</span>
        merupakan asumsi harga pasar/ekonomi lokal dan bukan hasil pengujian mutakhir. Nilai
        faktor emisi (EPD/ICE) bersifat referensi generik cradle-to-gate (A1–A3); mitra industri
        disarankan mengganti dengan EPD produsen aktual untuk klaim resmi.
      </div>

      {/* Seed Confirmation Dialog */}
      <Dialog open={seedOpen} onOpenChange={(o) => { if (!seeding) setSeedOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="dialog-seed-simulation">
          <DialogHeader>
            <DialogTitle className="font-cabinet flex items-center gap-2">
              <Database className="w-5 h-5 text-emerald-600" /> Seed Data Simulasi Ilmiah
            </DialogTitle>
          </DialogHeader>
          {!seedResult ? (
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Tindakan ini akan <b>membuat ulang</b> organisasi{" "}
                <code className="px-1 rounded bg-slate-100 text-slate-800">polimdo-simulasi</code>{" "}
                beserta seluruh master data, mix design, batch produksi, dan referensi ilmiahnya.
              </p>
              <ul className="text-xs text-slate-600 list-disc pl-5 space-y-0.5">
                <li>10 referensi ilmiah (ICE 2019, Aji 2021, SNI 03-0691-1996, UMP Sulut, dst.)</li>
                <li>2 mix design (konvensional + rendah karbon 20% FA + 5% LSP)</li>
                <li>6 batch produksi (3 CV + 3 LC) dengan variansi realistis</li>
                <li>8 perhitungan HPP + 6 skenario harga jual</li>
              </ul>
              <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
                Data lama di organisasi simulasi akan <b>dihapus</b>. Organisasi mitra lain tidak
                terpengaruh. Login demo: <code>simulasi@greencon.id</code> / <code>Simulasi#2026</code>.
              </div>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="font-semibold text-emerald-900">Seed berhasil dijalankan.</div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  Org ID: <code>{seedResult.org_id}</code>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="p-2 rounded-lg bg-slate-50 border">
                  <div className="text-slate-500 uppercase tracking-widest text-[10px] font-bold">Referensi</div>
                  <div className="font-bold text-slate-900">{seedResult.references_count}</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-50 border">
                  <div className="text-slate-500 uppercase tracking-widest text-[10px] font-bold">Batch</div>
                  <div className="font-bold text-slate-900">{seedResult.batches_count}</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-50 border">
                  <div className="text-slate-500 uppercase tracking-widest text-[10px] font-bold">HPP Calc</div>
                  <div className="font-bold text-slate-900">{seedResult.hpp_calcs_count}</div>
                </div>
                <div className="p-2 rounded-lg bg-slate-50 border">
                  <div className="text-slate-500 uppercase tracking-widest text-[10px] font-bold">Mix Design</div>
                  <div className="font-bold text-slate-900">{seedResult.mix_designs_count}</div>
                </div>
              </div>
              <div className="p-3 rounded-xl bg-slate-900 text-slate-100 text-xs font-mono">
                <div>CV avg HPP/unit: Rp {Number(seedResult.conventional?.avg_hpp_per_unit || 0).toLocaleString("id-ID")}</div>
                <div>LC avg HPP/unit: Rp {Number(seedResult.low_carbon?.avg_hpp_per_unit || 0).toLocaleString("id-ID")}</div>
                <div className="text-emerald-300 mt-1">
                  Δ HPP: {seedResult.hpp_delta_percent > 0 ? "+" : ""}{seedResult.hpp_delta_percent}% ·
                  Reduksi Emisi: {seedResult.emissions_reduction_percent}%
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            {!seedResult ? (
              <>
                <button
                  onClick={() => setSeedOpen(false)}
                  disabled={seeding}
                  className="h-9 px-4 rounded-lg text-sm font-semibold text-slate-600 hover:text-slate-900"
                  data-testid="btn-seed-cancel"
                >
                  Batal
                </button>
                <button
                  onClick={runSeed}
                  disabled={seeding}
                  className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold inline-flex items-center gap-2"
                  data-testid="btn-seed-confirm"
                >
                  {seeding ? <><Loader2 className="w-4 h-4 animate-spin" /> Menjalankan…</> : <><Database className="w-4 h-4" /> Jalankan Seed</>}
                </button>
              </>
            ) : (
              <button
                onClick={() => { setSeedOpen(false); setSeedResult(null); }}
                className="h-9 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
                data-testid="btn-seed-close"
              >
                Tutup
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
